import { inject, Injectable, OnDestroy } from '@angular/core';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';
import { Observable, ReplaySubject, Subscription } from 'rxjs';

import * as Sentry from '@sentry/angular';

import { UserIdService } from './user-id.service';
import { UserRolesService } from './user-roles.service';
import { AdminSettingsService } from './admin-settings.service';
import { SessionStateService } from './session-state.service';
import { SessionValidityService } from './session-validity.service';
import { UserActivityTrackingService } from './user-activity-tracking.service';
import { isSystemAdminIdentity } from '../util/google-sso';

@Injectable({
  providedIn: 'root'
})
export class AuthenticatedDataService<T> implements OnDestroy {
  private value$ = new ReplaySubject<T | undefined>(1);
  private value?: T | undefined;
  private valueCount$ = new ReplaySubject<number | undefined>(0);

  public subscriptions = new Array<Subscription>();

  private readonly sessionValidity = inject(SessionValidityService);

  constructor(
    protected auth: Auth,
    protected userIdService: UserIdService,
    protected userRolesService: UserRolesService,
  ) {
    const adminSettingsService = inject(AdminSettingsService);
    const sessionStateService = inject(SessionStateService);
    const userActivityTrackingService = inject(UserActivityTrackingService);

    this.refresh();

    onAuthStateChanged(this.auth, async (user: User | null) => {
      if (!user || sessionStateService.isSigningOut()) {
        // signed out, unsubscribe from all subscriptions
        this.cleanUp();

        this.userIdService.reset();
        this.userRolesService.reset();
        adminSettingsService.reset();
        userActivityTrackingService.stop();

        Sentry.setUser(null);
      } else {
        // signed in (probably signed out, and then back in), reconfigure all subscriptions
        await this.userIdService.set(user.uid);

        let claims: any = {};
        try {
          const currentUser = await this.auth.currentUser?.getIdTokenResult();
          claims = currentUser?.claims ?? {};
        } catch (e) {
          console.error('[AuthenticatedDataService] Failed to fetch ID token claims:', e);
        }

        if (sessionStateService.isSigningOut()) {
          this.cleanUp();
          return;
        }

        // Mirrors isSystemAdmin on the backend: the isAdmin claim only counts
        // on a staff address. Without this the UI would offer admin surfaces
        // that every backend call and security rule then rejects.
        const isSystemAdmin = isSystemAdminIdentity(claims.isAdmin, claims.email ?? user.email);

        await this.userRolesService.set(claims.roles, isSystemAdmin);

        if (isSystemAdmin) {
          adminSettingsService.load();
        } else {
          adminSettingsService.reset();
        }

        Sentry.setUser({
          id: user.uid,
        });

        void userActivityTrackingService.start();

        this.init();
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanUp();
  }

  public addSubscription(sub: Subscription) {
    this.subscriptions.push(sub);
  }

  public get(): T | undefined {
    return this.value;
  }

  public get$(): Observable<T | undefined> {
    return this.value$.asObservable();
  }

  public getCount$(): Observable<number | undefined> {
    return this.valueCount$.asObservable();
  }

  public async set(val: T) {
    this.value = val;
    await this.refresh();
  }

  public async refresh() {
    this.value$.next(await this.get());
    this.setValue();
  }

  public reset() {
    this.cleanUp();
  }

  private setValue() {
    if (!this.get()) {
      this.valueCount$.next(undefined);
      return;
    }

    if (Object.prototype.toString.call(this.get()) === '[object Array]') {
      this.valueCount$.next((this.get() as T[])?.length)
    }
  }

  protected async init() { }

  /**
   * Reports a Firestore listener failure from a root-scoped data service.
   *
   * These listeners run for the whole signed-in session regardless of the page,
   * which makes them the app's sentinels: if one of them is denied, it is worth
   * asking whether the session itself has ended rather than assuming the user
   * simply cannot see this particular data.
   */
  protected handleListenerError(source: string, error: unknown): void {
    console.error(`${source} listener error:`, error);
    this.sessionValidity.checkAfterPermissionDenied(error);
  }

  protected clearState() {
    this.value = undefined;
    this.value$.next(undefined);
    this.valueCount$.next(undefined);
  }

  protected clearSubscriptions() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    this.subscriptions = [];
  }

  protected cleanUp() {
    this.clearSubscriptions();
    this.clearState();
  }
}
